import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Phone, 
  Mail, 
  Calendar, 
  CreditCard,
  Clock,
  Trash2,
  Edit2,
  RefreshCw,
  Loader2,
  QrCode,
  CheckCircle2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }
};

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({ name: '', email: '', phone: '' });
  const [renewMembershipId, setRenewMembershipId] = useState('');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [clientRes, membershipsRes] = await Promise.all([
        axios.get(`${API}/api/clients/${id}`, { headers }),
        axios.get(`${API}/api/memberships`, { headers }),
      ]);
      setClient(clientRes.data);
      setMemberships(membershipsRes.data);
      setEditData({
        name: clientRes.data.name,
        email: clientRes.data.email || '',
        phone: clientRes.data.phone || ''
      });
    } catch (err) {
      toast.error('Cliente no encontrado');
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/api/clients/${id}`, editData, { headers });
      setClient({ ...client, ...editData });
      setShowEditModal(false);
      toast.success('Cliente actualizado');
    } catch (err) {
      toast.error('Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleRenew = async () => {
    if (!renewMembershipId) {
      toast.error('Selecciona una membresía');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(
        `${API}/api/clients/${id}/renew?membership_id=${renewMembershipId}`,
        {},
        { headers }
      );
      await fetchData();
      setShowRenewModal(false);
      toast.success('Membresía renovada');
    } catch (err) {
      toast.error('Error al renovar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/api/clients/${id}`, { headers });
      toast.success('Cliente eliminado');
      navigate('/clients');
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6 max-w-4xl">
          <div className="h-6 w-32 nova-skeleton" />
          <div className="h-48 nova-skeleton rounded-xl" />
          <div className="h-32 nova-skeleton rounded-xl" />
        </div>
      </Layout>
    );
  }

  if (!client) return null;

  const membership = memberships.find(m => m.id === client.membership_id);
  const daysRemaining = client.membership_end 
    ? Math.ceil((new Date(client.membership_end) - new Date()) / (1000 * 60 * 60 * 24))
    : -999;

  const getStatusInfo = () => {
    if (daysRemaining < 0) {
      return { 
        text: 'Vencido', 
        color: 'text-red-400', 
        bg: 'bg-gradient-to-br from-red-500/10 to-red-600/5', 
        border: 'border-red-500/20',
        icon: 'bg-red-500/15 text-red-400',
        glow: '0 0 30px rgba(239, 68, 68, 0.1)'
      };
    } else if (daysRemaining <= 7) {
      return { 
        text: `${daysRemaining} días restantes`, 
        color: 'text-amber-400', 
        bg: 'bg-gradient-to-br from-amber-500/10 to-amber-600/5', 
        border: 'border-amber-500/20',
        icon: 'bg-amber-500/15 text-amber-400',
        glow: '0 0 30px rgba(245, 158, 11, 0.1)'
      };
    } else {
      return { 
        text: 'Activo', 
        color: 'text-emerald-400', 
        bg: 'bg-gradient-to-br from-emerald-500/10 to-emerald-600/5', 
        border: 'border-emerald-500/20',
        icon: 'bg-emerald-500/15 text-emerald-400',
        glow: '0 0 30px rgba(34, 197, 94, 0.1)'
      };
    }
  };

  const status = getStatusInfo();

  return (
    <Layout>
      <motion.div 
        data-testid="client-detail" 
        className="space-y-6 max-w-4xl"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Back button */}
        <motion.div variants={item}>
          <Link 
            to="/clients" 
            className="inline-flex items-center gap-2 text-[var(--nova-text-tertiary)] hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Volver a clientes</span>
          </Link>
        </motion.div>

        {/* Header Card */}
        <motion.div variants={item} className="nova-card p-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="nova-avatar nova-avatar-xl">
                {client.name?.charAt(0)?.toUpperCase() || 'C'}
              </div>
              <div>
                <h1 className="text-headline text-white mb-1">{client.name}</h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-[var(--nova-text-tertiary)]">
                  {client.email && (
                    <span className="flex items-center gap-1.5">
                      <Mail className="w-4 h-4" />
                      {client.email}
                    </span>
                  )}
                  {client.phone && (
                    <span className="flex items-center gap-1.5">
                      <Phone className="w-4 h-4" />
                      {client.phone}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                data-testid="edit-client-button"
                className="nova-btn nova-btn-secondary"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowDeleteDialog(true)}
                data-testid="delete-client-button"
                className="p-2.5 bg-red-500/10 border border-red-500/20 rounded-xl hover:bg-red-500/20 transition-colors"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Membership Status */}
        <motion.div
          variants={item}
          className={`nova-card p-6 ${status.bg} border ${status.border}`}
          style={{ boxShadow: status.glow }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${status.icon}`}>
                  <CreditCard className="w-5 h-5" />
                </div>
                <span className={`text-lg font-semibold ${status.color}`}>{status.text}</span>
              </div>
              <p className="text-[var(--nova-text-secondary)] ml-[52px]">
                {membership ? membership.name : 'Sin membresía asignada'}
              </p>
              {client.membership_end && (
                <p className="text-sm text-[var(--nova-text-tertiary)] mt-1 ml-[52px]">
                  Vence: {new Date(client.membership_end).toLocaleDateString('es-MX', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              )}
            </div>
            <button
              onClick={() => setShowRenewModal(true)}
              data-testid="renew-button"
              className="nova-btn nova-btn-primary"
            >
              <RefreshCw className="w-4 h-4" />
              Renovar Membresía
            </button>
          </div>
        </motion.div>

        {/* Access Code */}
        <motion.div variants={item} className="nova-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-lg bg-indigo-500/15 flex items-center justify-center">
              <QrCode className="w-4 h-4 text-indigo-400" />
            </div>
            <h3 className="text-title text-white">Código de Acceso</h3>
          </div>
          <div className="flex items-center gap-5">
            <div className="px-8 py-5 bg-[var(--nova-bg-subtle)] border border-[var(--nova-border)] rounded-2xl">
              <span className="text-3xl font-mono font-bold text-white tracking-[0.4em]">
                {client.access_code}
              </span>
            </div>
            <p className="text-caption max-w-[180px]">
              Código único para check-in rápido en recepción
            </p>
          </div>
        </motion.div>

        {/* Access History */}
        <motion.div variants={item} className="nova-card p-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <Clock className="w-4 h-4 text-emerald-400" />
            </div>
            <h3 className="text-title text-white">Historial de Accesos</h3>
          </div>
          {client.access_history?.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {client.access_history.slice().reverse().map((access, i) => (
                <motion.div 
                  key={i} 
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-4 p-3.5 bg-[var(--nova-bg-subtle)] border border-[var(--nova-border-subtle)] rounded-xl hover:border-[var(--nova-border)] transition-colors"
                >
                  <div className="nova-status-dot nova-status-dot-success" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[var(--nova-text)]">Entrada registrada</p>
                    <p className="text-xs text-[var(--nova-text-tertiary)]">
                      {new Date(access.timestamp).toLocaleString('es-MX')}
                    </p>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500/40" />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="w-12 h-12 rounded-2xl bg-[var(--nova-surface-raised)] flex items-center justify-center mb-3">
                <Clock className="w-6 h-6 text-[var(--nova-text-quaternary)]" />
              </div>
              <p className="text-caption">No hay registros de acceso</p>
            </div>
          )}
        </motion.div>

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)] text-[var(--nova-text)] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-title">Editar Cliente</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Nombre</label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="nova-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Email</label>
                <input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  className="nova-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Teléfono</label>
                <input
                  type="tel"
                  value={editData.phone}
                  onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                  className="nova-input"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="nova-btn nova-btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={saving}
                  className="nova-btn nova-btn-primary flex-1"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Guardar
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Renew Modal */}
        <Dialog open={showRenewModal} onOpenChange={setShowRenewModal}>
          <DialogContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)] text-[var(--nova-text)] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-title">Renovar Membresía</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-[var(--nova-text-secondary)] mb-2">Selecciona membresía</label>
                <Select value={renewMembershipId} onValueChange={setRenewMembershipId}>
                  <SelectTrigger className="w-full bg-[var(--nova-bg-subtle)] border-[var(--nova-border)] text-[var(--nova-text)]">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)]">
                    {memberships.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name} - ${m.price} ({m.duration_days} días)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowRenewModal(false)}
                  className="nova-btn nova-btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRenew}
                  disabled={saving}
                  className="nova-btn nova-btn-primary flex-1"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Renovar
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirm */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent className="bg-[var(--nova-surface)] border-[var(--nova-border-strong)]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">¿Eliminar cliente?</AlertDialogTitle>
              <AlertDialogDescription className="text-[var(--nova-text-secondary)]">
                Esta acción no se puede deshacer. Se eliminarán todos los datos del cliente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="nova-btn nova-btn-secondary">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-500 text-white hover:bg-red-600 rounded-xl"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </motion.div>
    </Layout>
  );
}
